/**
 * LanguagePicker — Dropdown với search + flag icons
 *
 * Dùng trong tour welcome modal (step 2, 3) + tab Cấu hình (future reuse).
 * Keyboard navigation: ↑↓ select, Enter confirm, Esc close.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { LANGUAGES, searchLanguages, getLanguage, type Language } from '../../../utils/languages';

interface Props {
  value: string;                  // current lang code
  onChange: (code: string) => void;
  label?: string;                 // vd "Ngôn ngữ nguồn"
  placeholder?: string;           // vd "Chọn ngôn ngữ..."
  excludeCode?: string;           // không cho pick trùng (vd source !== target)
  disabled?: boolean;
  className?: string;
}

const LanguagePicker: React.FC<Props> = ({
  value,
  onChange,
  label,
  placeholder = 'Chọn ngôn ngữ...',
  excludeCode,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focusedIdx, setFocusedIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = getLanguage(value);

  const results = useMemo(() => {
    const all = searchLanguages(query);
    return excludeCode ? all.filter(l => l.code !== excludeCode) : all;
  }, [query, excludeCode]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setFocusedIdx(0);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[focusedIdx]) {
      e.preventDefault();
      handleSelect(results[focusedIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleSelect = (lang: Language) => {
    onChange(lang.code);
    setIsOpen(false);
    setQuery('');
  };

  // Scroll focused item into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${focusedIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [focusedIdx, isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(o => !o)}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2.5
          bg-white border-2 rounded-xl text-sm transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed border-slate-200' : 'cursor-pointer'}
          ${isOpen ? 'border-indigo-400 shadow-md shadow-indigo-100' : 'border-slate-200 hover:border-indigo-200'}
        `}
      >
        {selected ? (
          <span className="flex items-center gap-2.5 min-w-0">
            <img
              src={`https://flagcdn.com/24x18/${selected.countryCode}.png`}
              srcSet={`https://flagcdn.com/48x36/${selected.countryCode}.png 2x`}
              width="24"
              height="18"
              alt=""
              loading="lazy"
              className="rounded-sm shadow-sm shrink-0"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-medium text-slate-800 truncate">{selected.name}</span>
            <span className="text-xs text-slate-400 shrink-0">({selected.nameEn})</span>
          </span>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); setFocusedIdx(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Tìm ngôn ngữ (vd: Vietnamese, 日本語, en)..."
              className="flex-1 text-sm outline-none placeholder-slate-400"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-72 overflow-y-auto">
            {!query && (
              <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50">
                Phổ biến
              </div>
            )}
            {results.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">
                Không tìm thấy ngôn ngữ "{query}"
              </div>
            ) : (
              results.map((lang, idx) => {
                const isSelected = lang.code === value;
                const isFocused = idx === focusedIdx;
                return (
                  <button
                    key={lang.code}
                    data-idx={idx}
                    type="button"
                    onClick={() => handleSelect(lang)}
                    onMouseEnter={() => setFocusedIdx(idx)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-left transition-colors
                      ${isFocused ? 'bg-indigo-50' : ''}
                      ${isSelected ? 'bg-indigo-100' : ''}
                    `}
                  >
                    <img
                      src={`https://flagcdn.com/24x18/${lang.countryCode}.png`}
                      srcSet={`https://flagcdn.com/48x36/${lang.countryCode}.png 2x`}
                      width="24"
                      height="18"
                      alt=""
                      loading="lazy"
                      className="rounded-sm shadow-sm shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{lang.name}</div>
                      <div className="text-xs text-slate-500 truncate">{lang.nameEn}</div>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-indigo-600 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          {!query && results.length > 0 && (
            <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100 bg-slate-50 text-center">
              Gõ để tìm thêm ngôn ngữ khác (có 50+ ngôn ngữ)
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguagePicker;
